/**
 * Client-side help search over the knowledge pack for offline mode (D10-UI-004).
 *
 * Plain keyword scoring -- a title hit outweighs a body hit -- over the same
 * generated corpus the assistant's `lookup_help` tool reads. Pages and tours are
 * first cut to the user's own allowed lists, so offline mode never offers a page
 * or tour the sidebar would hide.
 */
import type { KbPage, KbSection, KbTour, SupportKb } from "../../api/support";

const STOP_WORDS = new Set([
  "a", "an", "and", "are", "can", "do", "does", "for", "from", "how", "i", "in", "is", "it",
  "me", "my", "of", "on", "or", "the", "to", "what", "where", "who", "why", "with", "you",
]);

export function queryTerms(query: string): string[] {
  return Array.from(
    new Set(
      query
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((t) => t.length >= 2 && !STOP_WORDS.has(t)),
    ),
  );
}

function score(terms: string[], title: string, body: string): number {
  const t = title.toLowerCase();
  const b = body.toLowerCase();
  let total = 0;
  for (const term of terms) {
    if (t.includes(term)) total += 3;
    if (b.includes(term)) total += 1;
  }
  return total;
}

function top<T>(items: T[], scoreOf: (item: T) => number, limit: number): T[] {
  return items
    .map((item) => ({ item, s: scoreOf(item) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.item);
}

export interface HelpResults {
  sections: KbSection[];
  pages: KbPage[];
  tours: KbTour[];
}

export function searchHelp(
  kb: SupportKb,
  query: string,
  allowedRoutes: readonly string[],
  allowedTours: readonly string[],
): HelpResults {
  const terms = queryTerms(query);
  if (terms.length === 0) return { sections: [], pages: [], tours: [] };
  const routes = new Set(allowedRoutes);
  const tours = new Set(allowedTours);
  return {
    sections: top(kb.sections, (s) => score(terms, s.title, s.text), 5),
    pages: top(
      kb.pages.filter((p) => routes.has(p.route)),
      (p) => score(terms, p.label, `${p.group} ${p.purpose}`),
      3,
    ),
    tours: top(
      kb.tours.filter((t) => tours.has(t.id)),
      (t) => score(terms, t.title, ""),
      3,
    ),
  };
}

// A page guide's first line lists the page's web paths ("Page address: /a, /b.").
const PAGE_ADDRESS_LINE = /^page address\s*:\s*(.*)$/i;
const PATH_IN_LINE = /\/[a-z0-9][a-z0-9/-]*/g;

export interface ArticleView {
  /** The article text without its "Page address" line: paths are never shown. */
  text: string;
  /** The article's page as an in-app link, only when it is one of the user's pages. */
  page: { route: string; label: string } | null;
}

/** True when `route` is drawn in the admin shell (the catalogue's `/admin` rule). */
function inAdminShell(route: string): boolean {
  return route === "/admin" || route.startsWith("/admin/");
}

/**
 * How an offline help article is shown (#4599): the raw "Page address" line is
 * dropped, and the first of its pages the user can open (an exact allowed
 * route that the corpus also lists as a page) becomes an in-app link labelled
 * with the page name. Nothing else in the text ever becomes a link.
 *
 * One guide documents one screen, so when its address line names that screen
 * in both shells (`/crm/training, /admin/training`), the copy on the side the
 * user is on now (`currentRoute`) is linked first (D10-UI-010): following the
 * link never moves an admin out of the admin shell, or the other way round.
 */
export function articleView(
  section: Pick<KbSection, "text">,
  pages: readonly Pick<KbPage, "route" | "label">[],
  allowedRoutes: readonly string[],
  currentRoute = "",
): ArticleView {
  const lines = section.text.split("\n");
  const kept: string[] = [];
  let page: ArticleView["page"] = null;
  const allowed = new Set(allowedRoutes);
  for (const line of lines) {
    const m = line.trim().match(PAGE_ADDRESS_LINE);
    if (!m) {
      kept.push(line);
      continue;
    }
    if (page) continue;
    const open: { route: string; label: string }[] = [];
    for (const raw of m[1]?.match(PATH_IN_LINE) ?? []) {
      const route = raw.length > 1 ? raw.replace(/\/+$/, "") : raw;
      const known = pages.find((p) => p.route === route);
      if (known && allowed.has(route)) open.push({ route, label: known.label });
    }
    const side = currentRoute ? inAdminShell(currentRoute) : null;
    page = open.find((p) => side !== null && inAdminShell(p.route) === side) ?? open[0] ?? null;
  }
  return { text: kept.join("\n").trim(), page };
}
